import React, { useRef, useState } from 'react'
import { WorkspaceCard } from './WorkspaceCard'
import { useAccessContext } from '../../context/AccessContext'
import { useI18n } from '../../i18n/I18nContext'
import {
  WORKER_EXPERIENCE_WORKSPACES,
  isWorkerOnlyMember,
  visibleWorkspaceNavigationEntries,
  workspaceLabel,
  type NavigationAccessInput,
  type WorkspaceNavigationEntry,
  type WorkspaceView,
} from './admin/navigationRegistry'

interface WorkspaceSwitcherProps {
  activeWorkspace: WorkspaceView
  onSelect: (workspace: WorkspaceView) => void
}

const workspaceGroupLabels: Partial<Record<WorkspaceNavigationEntry['group'], string>> = {
  platform: 'Platform',
  operations: 'Tenant operations',
  learning: 'Learning',
  tools: 'Tools',
  legacy: 'Operations',
}

const workspaceGroupOrder: WorkspaceNavigationEntry['group'][] = ['platform', 'operations', 'learning', 'tools', 'legacy']

export const WorkspaceSwitcher: React.FC<WorkspaceSwitcherProps> = ({ activeWorkspace, onSelect }) => {
  const [launcherOpen, setLauncherOpen] = useState(false)
  const launcherToggleRef = useRef<HTMLButtonElement | null>(null)
  const { accessContext, selectedTenantId, hasAnyPermission } = useAccessContext()
  const { t } = useI18n()
  const navigationContext: NavigationAccessInput = {
    accessContext,
    selectedTenantId,
    currentOrganization: null,
    hasAnyPermission,
  }
  const visibleWorkspaces = visibleWorkspaceNavigationEntries(navigationContext)
  // Flattened, group-ordered card list: cards render side by side in one
  // responsive grid, with the group surfaced as an eyebrow label on each card.
  const orderedWorkspaces = workspaceGroupOrder
    .flatMap((group) => visibleWorkspaces.filter((entry) => entry.group === group))
  /*
   * When is there genuinely something to switch to?
   *
   * Learning and worker mentorship are reachable from INSIDE the worker
   * application itself (its own navigation on desktop, the More screen on
   * phones), so for a worker they are not separate destinations to switch
   * between — they are part of the same experience. Learning is also available
   * to every authenticated user, so counting it would mean a worker always saw
   * a switcher, which is exactly the card that should not exist for them.
   *
   * So the card renders only when a worker-only account has an authorized
   * workspace OUTSIDE their own experience, and otherwise whenever more than
   * one workspace is authorized.
   */
  const outsideWorkerExperience = orderedWorkspaces
    .filter((entry) => !WORKER_EXPERIENCE_WORKSPACES.has(entry.key))
  const workerOnly = isWorkerOnlyMember(accessContext)
  if (workerOnly && outsideWorkerExperience.length === 0) return null
  if (orderedWorkspaces.length < 2) return null

  const selectWorkspace = (workspace: WorkspaceView) => {
    const restoreToggleFocus = launcherOpen
    setLauncherOpen(false)
    onSelect(workspace)
    if (restoreToggleFocus) {
      window.requestAnimationFrame(() => launcherToggleRef.current?.focus())
    }
  }

  return (
    <section className="switcher stack" data-testid="workspace-switcher">
      <div className="workspace-launcher-heading">
        <div>
          <h2 className="heading-lg">{t('Workspaces')}</h2>
          <p className="text-muted">
            <span className="workspace-launcher-desktop-copy">{t('Jump to the area you need.')}</span>
            <span className="workspace-launcher-mobile-copy">{t('Current workspace')}: {t(workspaceLabel(activeWorkspace, { t }))}</span>
          </p>
        </div>
        <button
          ref={launcherToggleRef}
          type="button"
          className="btn btn-secondary workspace-launcher-toggle"
          aria-expanded={launcherOpen}
          aria-controls="workspace-launcher-grid"
          onClick={() => setLauncherOpen((open) => !open)}
          data-testid="workspace-launcher-toggle"
        >
          {launcherOpen ? t('Close') : t('Switch')}
        </button>
      </div>

      <div
        id="workspace-launcher-grid"
        className={`switcher-grid ${launcherOpen ? 'switcher-grid--open' : ''}`}
      >
        {orderedWorkspaces.map((entry) => {
          const billingLocked = Boolean(entry.billingFeatureKey && accessContext?.billingEntitlements?.features?.[entry.billingFeatureKey]?.enabled !== true)
          return (
          <WorkspaceCard
            key={entry.key}
            group={t(workspaceGroupLabels[entry.group] || entry.group)}
            title={`${billingLocked ? '🔒 ' : ''}${t(entry.resolveLabel({ t }))}`}
            description={t(entry.resolveDescription({ t }))}
            cta={billingLocked ? t('Contact Sales') : t(entry.cta)}
            feature={entry.feature}
            hasAccessOverride
            disabled={entry.isDisabled?.(navigationContext)}
            active={activeWorkspace === entry.key}
            onClick={() => selectWorkspace(entry.key)}
            testId={`workspace-${entry.key}`}
          />
          )
        })}
      </div>
    </section>
  )
}
